import clsx from 'clsx';
import { useState } from 'react';
import { Button } from 'react-aria-components';

import { focusRingClassName } from '#/shared/utils/domHelper';

import { MaterialIcon } from './MaterialIcon';

type BannerVariant = 'default' | 'warning';

interface BannerProps {
  title: string;
  supportingText: string;
  className?: string;
  isDismissable?: boolean;
  variant?: BannerVariant;
  action?: {
    label: string;
    onPress: () => void;
  };
}

const variantStyles: Record<BannerVariant, string> = {
  default: 'border-l-8 border-primary bg-primary-surface-l1',
  warning: 'border-l-8 border-warning-tint bg-warning-surface-l1',
};

export function Banner({
  title,
  supportingText,
  className,
  isDismissable = false,
  variant = 'default',
  action,
}: Readonly<BannerProps>) {
  const [isVisible, setIsVisible] = useState(true);

  const handleOnPressDismiss = () => {
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <section className={clsx('flex flex-col p-16 pl-8 rounded-xs border-1', variantStyles[variant], className)}>
      <div className="flex items-start gap-xs">
        <MaterialIcon iconName="info" />

        <div className="flex flex-col grow gap-xs">
          <h3 className="text-16 leading-20 font-700 text-on-surface">{title}</h3>
          <p className="text-16 leading-20 font-400 text-on-surface-variant">{supportingText}</p>
          {!!action && (
            <Button
              className={clsx(
                'text-left',
                'text-primary-on-surface-variant text-16 leading-20 font-600',
                'hover:text-primary-on-surface hover:cursor-pointer',
                `focus:text-primary-on-surface ${focusRingClassName}`,
              )}
              onPress={action.onPress}
            >
              {action.label}
            </Button>
          )}
        </div>

        {isDismissable && (
          <Button
            className={clsx('flex rounded-xs hover:cursor-pointer', focusRingClassName)}
            aria-label="Dismiss"
            onPress={handleOnPressDismiss}
          >
            <MaterialIcon iconName="close" size="sm" />
          </Button>
        )}
      </div>
    </section>
  );
}
